import { Menu, MenuProps } from '@chakra-ui/react';
import { FC, PropsWithChildren, ReactNode, useCallback, useMemo, useRef } from 'react';

import { useDropdownState } from '../hooks/use-dropdown-state';
import { DropdownContext } from '../lib/context';

export interface DropdownProps extends Omit<MenuProps, 'children'>, PropsWithChildren {
	defaultValue?: any;
	id?: string;
	name?: string;
	onChange?: (value: any) => void;
	onScrollToBottom?: () => void;
	placeholder?: ReactNode;
	renderOption?: (value: any) => ReactNode;
	value?: any;
	valueToString?: (value: any) => string;
}

interface DropdownContextProviderProps extends PropsWithChildren {
	id?: string;
	isOpen: boolean;
	name?: string;
	onScrollToBottom?: () => void;
	placeholder?: ReactNode;
	renderOption?: (value: any) => ReactNode;
	valueToString?: (value: any) => string;
	onChange: (value: any) => void;
	value: any;
}

/**
 * Wraps `DropdownContext` and memoizes its value so consumers (e.g. each `DropdownOption`)
 * don't re-render every time the parent re-renders or the menu opens/closes.
 *
 * Callback-shaped props (`onScrollToBottom`, `renderOption`, `valueToString`, `onChange`) are
 * wrapped via refs so consumers always invoke the latest handler without the memoized context
 * value invalidating on every render. This means the context value reference only changes when
 * a *visible* prop actually changes (e.g. `value`, `isOpen`, `placeholder`).
 */
const DropdownContextProvider: FC<DropdownContextProviderProps> = ({
	id,
	isOpen,
	name,
	onScrollToBottom,
	placeholder,
	renderOption,
	valueToString,
	onChange,
	value,
	children,
}) => {
	const callbacksRef = useRef({ onScrollToBottom, renderOption, valueToString, onChange });
	callbacksRef.current = { onScrollToBottom, renderOption, valueToString, onChange };

	const stableOnScrollToBottom = useCallback(() => callbacksRef.current.onScrollToBottom?.(), []);
	const stableRenderOption = useCallback((optionValue: any) => callbacksRef.current.renderOption?.(optionValue), []);
	const stableValueToString = useCallback((optionValue: any) => callbacksRef.current.valueToString?.(optionValue) ?? '', []);
	const stableOnChange = useCallback((nextValue: any) => callbacksRef.current.onChange(nextValue), []);

	// Track only the *presence* of each optional callback prop, not its reference identity.
	// Most callers pass inline arrow functions (e.g. `renderOption={() => ...}`) that change
	// reference every render; depending on those references would invalidate `contextValue` on
	// every parent render and defeat the ref-based memoization strategy this component is built
	// around. The actual latest callback is always invoked via `callbacksRef`, so consumers still
	// get up-to-date behavior — we only need to know whether a callback was provided at all so
	// the ternary in the memo body emits `undefined` vs the stable wrapper consistently.
	const hasOnScrollToBottom = !!onScrollToBottom;
	const hasRenderOption = !!renderOption;
	const hasValueToString = !!valueToString;

	const contextValue = useMemo(
		() => ({
			id,
			isOpen,
			name,
			placeholder,
			value,
			onScrollToBottom: hasOnScrollToBottom ? stableOnScrollToBottom : undefined,
			renderOption: hasRenderOption ? stableRenderOption : undefined,
			valueToString: hasValueToString ? stableValueToString : undefined,
			onChange: stableOnChange,
		}),
		[
			id,
			isOpen,
			name,
			placeholder,
			value,
			hasOnScrollToBottom,
			hasRenderOption,
			hasValueToString,
			stableOnScrollToBottom,
			stableRenderOption,
			stableValueToString,
			stableOnChange,
		]
	);

	return <DropdownContext value={contextValue}>{children}</DropdownContext>;
};

export const Dropdown: FC<DropdownProps> = ({
	children,
	defaultValue,
	id,
	name,
	onChange,
	onScrollToBottom,
	placeholder,
	renderOption,
	value,
	valueToString,
	...rest
}) => {
	const state = useDropdownState({ defaultValue, onChange, value });

	return (
		<Menu autoSelect={false} offset={[0, 2]} placement="bottom-end" {...rest}>
			{({ isOpen }) => (
				<DropdownContextProvider
					id={id}
					isOpen={isOpen}
					name={name}
					onScrollToBottom={onScrollToBottom}
					placeholder={placeholder}
					renderOption={renderOption}
					valueToString={valueToString}
					onChange={state.onChange}
					value={state.value}
				>
					{children}
				</DropdownContextProvider>
			)}
		</Menu>
	);
};
